import { useId, useMemo, useRef, useState } from 'react';

import { DialogShell } from '../../../components/DialogShell';

import { isVectorSource } from '../../../data/model';
import { effectiveVectorSourceForLayer, fieldDisplayLabel } from '../../../joins/effectiveLayer';
import type { LayerDefinition, LayerId } from '../../../project/model';
import {
  attributeRuleConditionWithField,
  attributeRuleConditionWithOperator,
  createDefaultAttributeRuleCondition,
  createDefaultAttributeRuleGroup,
} from '../../../rules/editing';
import { attributeRuleField, attributeRuleFields } from '../../../rules/fields';
import type {
  AttributeRuleCondition,
  AttributeRuleGroup,
  AttributeRuleOperator,
} from '../../../rules/model';
import {
  attributeRuleOperatorDefinition,
  attributeRuleOperatorsForField,
} from '../../../rules/operators';
import {
  matchingSelectableFeatures,
  MAX_ATTRIBUTE_QUERY_CONDITIONS,
} from '../../../selection/attributeQuery';
import type { SelectionMutation } from '../../../selection/model';
import { useAppStore } from '../../state/useAppStore';

interface SelectByAttributeDialogProps {
  readonly onClose: () => void;
}

type QueryAction = Exclude<SelectionMutation, 'toggle'>;

function usableLayerContext(
  layers: readonly LayerDefinition[],
  sources: ReturnType<typeof useProjectSources>,
  layerId: LayerId,
) {
  const layer = layers.find((candidate) => candidate.id === layerId);
  const canonicalCandidate = layer
    ? sources.find((source) => source.id === layer.sourceId)
    : undefined;
  const effective = layer ? effectiveVectorSourceForLayer(sources, layer) : null;
  return layer &&
    canonicalCandidate &&
    isVectorSource(canonicalCandidate) &&
    effective &&
    attributeRuleFields(effective.fields).length > 0
    ? { layer, canonical: canonicalCandidate, effective }
    : null;
}

function useProjectSources() {
  return useAppStore((state) => state.project.sources);
}

function QueryConditionRow({
  condition,
  fields,
  index,
  onChange,
  onRemove,
}: {
  readonly condition: AttributeRuleCondition;
  readonly fields: ReturnType<typeof attributeRuleFields>;
  readonly index: number;
  readonly onChange: (condition: AttributeRuleCondition) => void;
  readonly onRemove: () => void;
}) {
  const field = attributeRuleField(fields, condition.field);
  if (!field) return null;
  const definition = attributeRuleOperatorDefinition(field, condition.operator);
  const number = index + 1;
  return (
    <div className="attribute-query-condition">
      <select
        aria-label={`Attribute query field ${number}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithField(condition, event.target.value, fields);
          if (next) onChange(next);
        }}
        value={condition.field}
      >
        {fields.map((candidate) => (
          <option key={candidate.name} value={candidate.name}>
            {fieldDisplayLabel(candidate)}
          </option>
        ))}
      </select>
      <select
        aria-label={`Attribute query operator ${number}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithOperator(
            condition,
            event.target.value as AttributeRuleOperator,
            fields,
          );
          if (next) onChange(next);
        }}
        value={condition.operator}
      >
        {attributeRuleOperatorsForField(field).map((operator) => (
          <option key={operator.operator} value={operator.operator}>
            {operator.label}
          </option>
        ))}
      </select>
      {definition?.valueKind === 'number' ? (
        <input
          aria-label={`Attribute query value ${number}`}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (Number.isFinite(value)) onChange({ ...condition, value });
          }}
          type="number"
          value={typeof condition.value === 'number' ? condition.value : 0}
        />
      ) : definition?.valueKind === 'string' ? (
        <input
          aria-label={`Attribute query value ${number}`}
          onChange={(event) => onChange({ ...condition, value: event.target.value })}
          type="text"
          value={typeof condition.value === 'string' ? condition.value : ''}
        />
      ) : null}
      <button
        aria-label={`Remove attribute query condition ${number}`}
        onClick={onRemove}
        type="button"
      >
        ×
      </button>
    </div>
  );
}

export function SelectByAttributeDialog({ onClose }: SelectByAttributeDialogProps) {
  const layers = useAppStore((state) => state.project.layers);
  const sources = useProjectSources();
  const selectedLayerId = useAppStore((state) => state.shell.selectedLayerId);
  const runQuery = useAppStore((state) => state.selectFeaturesByAttribute);
  const candidates = useMemo(
    () =>
      layers.filter((layer) => {
        const source = sources.find((candidate) => candidate.id === layer.sourceId);
        const effective = effectiveVectorSourceForLayer(sources, layer);
        return (
          source &&
          isVectorSource(source) &&
          effective &&
          attributeRuleFields(effective.fields).length > 0
        );
      }),
    [layers, sources],
  );
  const initialLayer = candidates.some((layer) => layer.id === selectedLayerId)
    ? selectedLayerId
    : (candidates[0]?.id ?? null);
  const initialContext = initialLayer ? usableLayerContext(layers, sources, initialLayer) : null;
  const [layerId, setLayerId] = useState<LayerId | null>(initialLayer);
  const [query, setQuery] = useState<AttributeRuleGroup | null>(() =>
    initialContext ? createDefaultAttributeRuleGroup(initialContext.effective.fields) : null,
  );
  const [action, setAction] = useState<QueryAction>('replace');
  const [notice, setNotice] = useState<string | null>(null);
  const layerSelectRef = useRef<HTMLSelectElement | null>(null);
  const titleId = useId();
  const context = layerId ? usableLayerContext(layers, sources, layerId) : null;
  const fields = context ? attributeRuleFields(context.effective.fields) : [];
  const conditions =
    query?.children.filter(
      (child): child is AttributeRuleCondition => child.kind === 'condition',
    ) ?? [];
  const matches =
    context && query
      ? matchingSelectableFeatures(context.layer, context.canonical, context.effective, query)
      : null;

  const chooseLayer = (nextLayerId: LayerId) => {
    const next = usableLayerContext(layers, sources, nextLayerId);
    setLayerId(nextLayerId);
    setQuery(next ? createDefaultAttributeRuleGroup(next.effective.fields) : null);
    setNotice(null);
  };
  const updateCondition = (index: number, condition: AttributeRuleCondition) => {
    setQuery({
      kind: 'group',
      operator: 'and',
      children: conditions.map((item, itemIndex) => (itemIndex === index ? condition : item)),
    });
  };
  const addCondition = () => {
    const condition = createDefaultAttributeRuleCondition(fields);
    if (condition)
      setQuery({ kind: 'group', operator: 'and', children: [...conditions, condition] });
  };

  return (
    <DialogShell
      className="new-map-dialog attribute-query-dialog"
      initialFocusRef={layerSelectRef}
      onClose={onClose}
      titleId={titleId}
    >
      <h2 id={titleId}>Select by attribute</h2>
      <label className="attribute-query-control">
        <span>Layer</span>
        <select
          aria-label="Attribute query layer"
          onChange={(event) => chooseLayer(event.target.value)}
          ref={layerSelectRef}
          value={layerId ?? ''}
        >
          {candidates.map((layer) => (
            <option key={layer.id} value={layer.id}>
              {layer.name}
            </option>
          ))}
        </select>
      </label>
      <fieldset>
        <legend>Select features where</legend>
        {conditions.map((condition, index) => (
          <div key={`${condition.field}:${index}`}>
            {index > 0 ? <span className="attribute-query-and">AND</span> : null}
            <QueryConditionRow
              condition={condition}
              fields={fields}
              index={index}
              onChange={(next) => updateCondition(index, next)}
              onRemove={() => {
                const next = conditions.filter((_, itemIndex) => itemIndex !== index);
                setQuery(next.length ? { kind: 'group', operator: 'and', children: next } : null);
              }}
            />
          </div>
        ))}
        <button
          disabled={conditions.length >= MAX_ATTRIBUTE_QUERY_CONDITIONS || fields.length === 0}
          onClick={addCondition}
          type="button"
        >
          + Add condition
        </button>
      </fieldset>
      <p className="attribute-query-count" role="status">
        {matches
          ? `${matches.length.toLocaleString()} selectable ${matches.length === 1 ? 'feature' : 'features'} match`
          : 'Complete a valid condition to see matches'}
      </p>
      <label className="attribute-query-control">
        <span>Selection action</span>
        <select
          aria-label="Selection action"
          onChange={(event) => setAction(event.target.value as QueryAction)}
          value={action}
        >
          <option value="replace">Replace selection</option>
          <option value="add">Add to selection</option>
          <option value="remove">Remove from selection</option>
        </select>
      </label>
      {notice ? (
        <p className="style-notice" role="alert">
          {notice}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={!context || !query || !matches || (matches.length === 0 && action !== 'remove')}
          onClick={() => {
            if (!context || !query) return;
            const result = runQuery(context.layer.id, query, action);
            if (result.ok) onClose();
            else setNotice(result.message);
          }}
          type="button"
        >
          Select
        </button>
      </div>
    </DialogShell>
  );
}
